// Twin of town-square/src/hooks/useDmAttachments.ts — edit both or neither.
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useApolloClient } from '@apollo/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { createUploadSession, type MediaDropProps, type UploadContext, type UploadItem } from '@arthelper/ui';
import { useAnalytics } from '~/context/analytics';
import { failureProperties, stepOf } from '@arthelper/shared/upload-failure';
import {
  isAllowedDmAttachment,
  isAllowedDmFile,
  MAX_DM_ATTACHMENTS,
  MAX_DM_ATTACHMENT_BYTES,
  MAX_DM_FILE_BYTES,
  DM_ATTACHMENT_ACCEPT,
  uploadDmImage,
  uploadDmFile,
  type DmFileAttachment,
} from '~/utils/dmImageUpload';

export type DmAttachment = UploadItem;

export function useDmAttachments() {
  const client = useApolloClient();
  const {
    amplitude: { track },
  } = useAnalytics();
  const { t } = useTranslation('social');
  const [session] = useState(() => createUploadSession());
  const disposalTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attachments = useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);

  const uploader = useCallback(
    async (file: File, ctx: UploadContext) => {
      if (file.type.startsWith('image/'))
        return uploadDmImage(
          client,
          file,
          (imageId) => track('image_uploaded', { image_id: imageId, source: 'direct_message' }),
          ctx
        );
      return uploadDmFile(client, file, ctx);
    },
    [client, track]
  );

  const dropProps = useMemo<MediaDropProps>(
    () => ({
      session,
      uploader,
      accept: DM_ATTACHMENT_ACCEPT.split(','),
      maxFiles: MAX_DM_ATTACHMENTS,
      maxSizeMB: { image: MAX_DM_ATTACHMENT_BYTES / 1_048_576, file: MAX_DM_FILE_BYTES / 1_048_576 },
      concurrency: 3,
      overlayPrompt: t('dm.attachments.drop-prompt', 'Images and documents attach to this message'),
      validate: (file) =>
        isAllowedDmAttachment(file) || isAllowedDmFile(file)
          ? null
          : t('dm.attachments.invalid', 'Only JPEG, PNG, PDF, documents, and spreadsheets can be attached'),
      labels: {
        failed: t('dm.attachments.failed-short', 'Failed'),
        retry: t('dm.attachments.retry', 'Retry'),
        remove: () => t('dm.attachments.remove', 'Remove attachment'),
        chooseFiles: () => t('dm.attachments.add', 'Attach a file'),
        release: (count) => t('dm.attachments.release', 'Release to add {{count}} files', { count }),
        uploading: (done, total) => t('dm.attachments.uploading', 'Uploading {{done}} of {{total}}', { done, total }),
        added: (total) => t('dm.attachments.added', '{{count}} files added', { count: total }),
        dropMore: t('dm.attachments.add', 'Attach a file'),
        status: {
          queued: t('dm.attachments.queued', 'Waiting'),
          transferring: t('dm.attachments.uploading-short', 'Uploading'),
          processing: t('dm.attachments.processing', 'Processing…'),
          transcoding: t('dm.attachments.processing', 'Processing…'),
          done: t('dm.attachments.added-short', 'Added'),
          error: t('dm.attachments.failed-short', 'Failed'),
        },
      },
      onRejected: (rejections) => {
        const first = rejections[0];
        if (!first) return;
        if (first.reason === 'size')
          toast.error(
            t(
              'dm.attachments.too-large',
              'That file is too large. Images must be {{imageMb}} MB or smaller, and documents must be {{fileMb}} MB or smaller.',
              { imageMb: MAX_DM_ATTACHMENT_BYTES / 1_048_576, fileMb: MAX_DM_FILE_BYTES / 1_048_576 }
            )
          );
        else if (first.reason === 'count')
          toast.error(t('dm.attachments.too-many', 'You can attach up to {{max}} files', { max: MAX_DM_ATTACHMENTS }));
        else
          toast.error(t('dm.attachments.invalid', 'Only JPEG, PNG, PDF, documents, and spreadsheets can be attached'));
        for (const rejection of rejections) {
          if (rejection.file.type.startsWith('image/'))
            track('image_upload_failed', failureProperties('pick', rejection.file, rejection.message));
        }
      },
      onItemError: (item, error) => {
        toast.error(t('dm.attachments.upload-failed', 'Could not upload an attachment'));
        if (item.kind === 'image') track('image_upload_failed', failureProperties(stepOf(error), item.file, error));
      },
    }),
    [session, uploader, t, track]
  );

  useEffect(() => {
    if (disposalTimer.current) clearTimeout(disposalTimer.current);
    return () => {
      disposalTimer.current = setTimeout(() => session.dispose(), 0);
    };
  }, [session]);

  const addFiles = useCallback(
    (files: File[]) => {
      session.configure(dropProps);
      void session.addFiles(files);
    },
    [session, dropProps]
  );
  const uploading = attachments.some(
    (item) => item.phase === 'queued' || item.phase === 'transferring' || item.phase === 'processing'
  );
  const imageIds = attachments
    .filter((item) => item.phase === 'done' && item.kind === 'image')
    .map((item) => (item.result as { imageId: string }).imageId);
  const fileAttachments = attachments
    .filter((item) => item.phase === 'done' && item.kind === 'file')
    .map((item) => item.result as DmFileAttachment);
  return {
    attachments,
    addFiles,
    clear: session.clear,
    uploading,
    imageIds,
    fileAttachments,
    session,
    dropProps,
  };
}
